import { axiosBasic } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { formatPeriodDate } from "../dashboard/statistics/dashboardStatisticsUtils";

const { AdminPayrollStatistics } = config.REST_API;

const get = (obj, ...keys) => {
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const str = (v) => (v == null ? "" : String(v));

/** @param {Date|null} periodFrom @param {Date|null} periodTo */
export function buildStatisticsParams(periodFrom, periodTo) {
  const params = {};
  if (periodFrom && periodTo) {
    params.periodStart = formatPeriodDate(periodFrom);
    params.periodEnd = formatPeriodDate(periodTo);
  }
  return params;
}

function normalizeCostBreakdownItem(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  return {
    key: str(get(r, "key", "Key")),
    label: str(get(r, "label", "Label")),
    amount: num(get(r, "amount", "Amount")),
    percentOfGross: num(get(r, "percentOfGross", "PercentOfGross")),
    value: num(get(r, "value", "Value")),
    percent: num(get(r, "percent", "Percent")),
  };
}

function normalizeCostSummary(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const breakdown = get(r, "costBreakdown", "CostBreakdown");
  return {
    periodStart: str(get(r, "periodStart", "PeriodStart")),
    periodEnd: str(get(r, "periodEnd", "PeriodEnd")),
    payrollRunCount: num(get(r, "payrollRunCount", "PayrollRunCount")),
    employeePayrollCount: num(get(r, "employeePayrollCount", "EmployeePayrollCount")),
    uniqueEmployeeCount: num(get(r, "uniqueEmployeeCount", "UniqueEmployeeCount")),
    totalGrossPay: num(get(r, "totalGrossPay", "TotalGrossPay")),
    totalNetPay: num(get(r, "totalNetPay", "TotalNetPay")),
    totalDeductions: num(get(r, "totalDeductions", "TotalDeductions")),
    totalBaseSalary: num(get(r, "totalBaseSalary", "TotalBaseSalary")),
    totalOvertimePay: num(get(r, "totalOvertimePay", "TotalOvertimePay")),
    totalBonusAmount: num(get(r, "totalBonusAmount", "TotalBonusAmount")),
    totalTaxesAmount: num(get(r, "totalTaxesAmount", "TotalTaxesAmount")),
    totalBenefitsAmount: num(get(r, "totalBenefitsAmount", "TotalBenefitsAmount")),
    committedSalaryAmount: num(get(r, "committedSalaryAmount", "CommittedSalaryAmount")),
    payrollToCommitmentRatioPercent: num(
      get(r, "payrollToCommitmentRatioPercent", "PayrollToCommitmentRatioPercent")
    ),
    deductionRatioPercent: num(get(r, "deductionRatioPercent", "DeductionRatioPercent")),
    netToGrossRatioPercent: num(get(r, "netToGrossRatioPercent", "NetToGrossRatioPercent")),
    costBreakdown: Array.isArray(breakdown) ? breakdown.map(normalizeCostBreakdownItem) : [],
  };
}

function normalizeStructureUsage(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const id = get(r, "structureId", "StructureId");
  return {
    structureId: id == null ? null : str(id),
    structureName: str(get(r, "structureName", "StructureName")),
    status: get(r, "status", "Status") == null ? null : str(get(r, "status", "Status")),
    employeePayrollCount: num(get(r, "employeePayrollCount", "EmployeePayrollCount")),
    uniqueEmployeeCount: num(get(r, "uniqueEmployeeCount", "UniqueEmployeeCount")),
    totalGrossPay: num(get(r, "totalGrossPay", "TotalGrossPay")),
    totalNetPay: num(get(r, "totalNetPay", "TotalNetPay")),
    percentOfTotalGross: num(get(r, "percentOfTotalGross", "PercentOfTotalGross")),
  };
}

function normalizeStructureCatalogItem(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const id = get(r, "structureId", "StructureId");
  return {
    structureId: id == null ? null : str(id),
    name: str(get(r, "name", "Name")),
    status: get(r, "status", "Status") == null ? null : str(get(r, "status", "Status")),
    paymentCycle: str(get(r, "paymentCycle", "PaymentCycle")),
    assignedEmployeeCount: num(get(r, "assignedEmployeeCount", "AssignedEmployeeCount")),
    committedGrossSalary: num(get(r, "committedGrossSalary", "CommittedGrossSalary")),
  };
}

function normalizeStructures(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const usage = get(r, "usageByStructure", "UsageByStructure");
  const catalog = get(r, "catalog", "Catalog");
  return {
    totalStructures: num(get(r, "totalStructures", "TotalStructures")),
    draftCount: num(get(r, "draftCount", "DraftCount")),
    publishedCount: num(get(r, "publishedCount", "PublishedCount")),
    archivedCount: num(get(r, "archivedCount", "ArchivedCount")),
    activeAssignmentCount: num(get(r, "activeAssignmentCount", "ActiveAssignmentCount")),
    usageByStructure: Array.isArray(usage) ? usage.map(normalizeStructureUsage) : [],
    catalog: Array.isArray(catalog) ? catalog.map(normalizeStructureCatalogItem) : [],
  };
}

function normalizeTrendPoint(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  return {
    periodStart: str(get(r, "periodStart", "PeriodStart")),
    periodEnd: str(get(r, "periodEnd", "PeriodEnd")),
    label: str(get(r, "label", "Label")),
    payrollRunCount: num(get(r, "payrollRunCount", "PayrollRunCount")),
    employeePayrollCount: num(get(r, "employeePayrollCount", "EmployeePayrollCount")),
    grossPay: num(get(r, "grossPay", "GrossPay")),
    netPay: num(get(r, "netPay", "NetPay")),
    deductions: num(get(r, "deductions", "Deductions")),
    payrollToCommitmentRatioPercent: num(
      get(r, "payrollToCommitmentRatioPercent", "PayrollToCommitmentRatioPercent")
    ),
  };
}

function normalizeGraphSeriesPoint(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  return {
    periodStart: str(get(r, "periodStart", "PeriodStart")),
    label: str(get(r, "label", "Label")),
    value: num(get(r, "value", "Value")),
  };
}

function normalizeGraphSeries(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const points = get(r, "points", "Points");
  return {
    key: str(get(r, "key", "Key")),
    label: str(get(r, "label", "Label")),
    points: Array.isArray(points) ? points.map(normalizeGraphSeriesPoint) : [],
  };
}

function normalizeCategoryBar(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const id = get(r, "categoryId", "CategoryId");
  return {
    categoryId: id == null ? null : str(id),
    categoryLabel: str(get(r, "categoryLabel", "CategoryLabel")),
    value: num(get(r, "value", "Value")),
  };
}

function normalizeStatusSlice(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  return {
    key: str(get(r, "key", "Key")),
    label: str(get(r, "label", "Label")),
    value: num(get(r, "value", "Value")),
    percent: num(get(r, "percent", "Percent")),
  };
}

function normalizeGraphs(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const costBreakdown = get(r, "costBreakdown", "CostBreakdown");
  const payrollTrend = get(r, "payrollTrend", "PayrollTrend");
  const grossByStructure = get(r, "grossByStructure", "GrossByStructure");
  const runsByStatus = get(r, "runsByStatus", "RunsByStatus");
  return {
    periodStart: str(get(r, "periodStart", "PeriodStart")),
    periodEnd: str(get(r, "periodEnd", "PeriodEnd")),
    costBreakdown: Array.isArray(costBreakdown) ? costBreakdown.map(normalizeCostBreakdownItem) : [],
    payrollTrend: Array.isArray(payrollTrend) ? payrollTrend.map(normalizeGraphSeries) : [],
    grossByStructure: Array.isArray(grossByStructure) ? grossByStructure.map(normalizeCategoryBar) : [],
    runsByStatus: Array.isArray(runsByStatus) ? runsByStatus.map(normalizeStatusSlice) : [],
  };
}

export function normalizePayrollDashboard(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const trends = get(r, "trends", "Trends");
  return {
    periodStart: str(get(r, "periodStart", "PeriodStart")),
    periodEnd: str(get(r, "periodEnd", "PeriodEnd")),
    costSummary: normalizeCostSummary(get(r, "costSummary", "CostSummary") ?? {}),
    structures: normalizeStructures(get(r, "structures", "Structures") ?? {}),
    trends: Array.isArray(trends) ? trends.map(normalizeTrendPoint) : [],
    graphs: normalizeGraphs(get(r, "graphs", "Graphs") ?? {}),
  };
}

function unwrapOne(res) {
  const body = res?.data;
  if (body == null || body.success === false) {
    return { data: null, message: body?.message };
  }
  return { data: body.data ?? null, message: body.message };
}

export async function fetchPayrollDashboard(periodFrom, periodTo) {
  const res = await axiosBasic.get(AdminPayrollStatistics.Dashboard, {
    params: buildStatisticsParams(periodFrom, periodTo),
  });
  const { data, message } = unwrapOne(res);
  return { dashboard: data ? normalizePayrollDashboard(data) : null, message };
}

export function formatPayrollMoney(value, currencyCode = "") {
  const n = Number(value);
  const formatted = Number.isFinite(n)
    ? new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)
    : "0.00";
  return currencyCode ? `${formatted} ${currencyCode}` : formatted;
}

export function formatPercent(value, digits = 1) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(digits)}%`;
}

/** Normalize trend bar heights (0–100) from gross pay values */
export function trendBarHeights(trends, valueKey = "grossPay") {
  if (!Array.isArray(trends) || trends.length === 0) return [];
  const values = trends.map((t) => num(t[valueKey]));
  const max = Math.max(...values, 1);
  return values.map((v) => Math.round((v / max) * 100));
}
